import { navigate } from "@reach/router";
import { Button } from "newui/components/Button";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Flex, useDisclosure } from "@chakra-ui/react";
import { Documentation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Documentation";
import { getDocumentType, getRFQType } from "actions/RFQ/rfqsAction";
import { Form, Formik } from "formik";
import Quote from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/Quote/Quote";
import { PreviewRFQ } from "../../../../pages/RFQ/RFQ/CreateRFQ/PreviewRFQ";
import {
	formValuesGenerator,
	getTabLists,
	validationSchema,
	validStepChecker,
	isQuoteFormInvalid,
} from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/createRFQUtils";
import TextButton from "newui/components/Button/TextButton";
import { NotFound } from "newui/features/ErrorPages/NotFound";
import { useSelector } from "react-redux";
import { showToast } from "utils/helpers";
import { RfxContext } from "newui/features/Rfx/context/RfxContext";
import useRefreshWarning from "hooks/useRefreshWarning";
import { TemplateDetail } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/TemplateDetail";
import { RFQTabNavigation } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/RFQTabNavigation";
import {
	createTemplate,
	getTemplateById,
	saveRFXTemplate,
} from "actions/RFQ/templateAction";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import ContinueButton from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/useContinueButton";
import { CreateRFxHeaderButtons } from "../../../../pages/RFQ/RFQ/CreateRFQ/CreateRFQPage/_components/CreateRFxHeaderButtons";
import InvitePreApprovalDrawer from "components/Pages/RFQDetail/InvitePreApprovalDrawer/InvitePreApprovalDrawer";
import { logErrorIfDebug } from "utils/logging";
import SavingOverlay from "newui/components/SavingOverlay";
import { replaceNewInUrlWithId } from "helpers/linkHelper";
import ReturnableSchedule from "./components/ReturnableSchedule/ReturnableSchedule";
import {
	mergeCreateRFxFormData,
	processFormValuesForSaving,
} from "./data-processor";
import ProjectDetail from "./components/ProjectDetail";
import { useRfxSubmissionData } from "../data-provider/RfxSubmissionDataProvider";
import { useAuthContext } from "newui/features/Auth/context/AuthContextProvider";

const TAB_NAMES = [
	"Rfx Details",
	"Documentation",
	"Returnable Schedules",
	"Quote",
	"RFx Participants",
];

const CreateRfxTemplate = ({ isEdit, rfxTemplateRouteId: urlTemplateId }) => {
	const [selectedTab, setSelectedTab] = useState(1);
	const { rfxData, updateLocalRfxData } = useRfxSubmissionData();

	const [templateId, setTemplateId] = useState(urlTemplateId || null);
	const tabButtonList = getTabLists(true, rfxData?.rfxType);
	const [hasTemplateLoaded, sethasTemplateLoaded] = useState(false);
	const formikRef = useRef();
	const [isSubmittingForm, setIsSubmittingForm] = useState(false);
	const [isValidForm, setIsValidForm] = useState(true);
	const [rfxTypes, setRFxTypes] = useState([]);
	const [documentCategories, setDocumentCategories] = useState([]);
	const [isNotFound, setIsNotFound] = useState(false);
	const [completedSteps, setCompletedSteps] = useState([]);
	const { company_id } = useSelector((state) => state?.auth?.user);
	const [touchedTabs, setTouchedTabs] = useState([]);
	const [isFormInitialized, setIsFormInitialized] = useState(false);
	const { clientPaths } = useAuthContext();

	const { onOpen, onClose, isOpen } = useDisclosure();
	const {
		onOpen: onApprovalOpen,
		onClose: onApprovalClose,
		isOpen: isApprovalOpen,
	} = useDisclosure();

	const activeTab = tabButtonList.find((tab) => tab.value === selectedTab);
	const lastStep = selectedTab === 4;
	const [openModal, setOpenModal] = useState(false);
	const enableOnChangeFormik = selectedTab !== 3;

	const { uploadInProgresses } = useContext(RfxContext);
	const isFileUploading = uploadInProgresses?.rfxProcurer?.length > 0;

	useRefreshWarning(isFileUploading);

	const tabRef = useRef(null);

	const [staticOptionsLoaded, setStaticOptionsLoaded] = useState(false);

	const fetchTemplateDataOnce = useCallback(async () => {
		if (templateId === "new") {
			return;
		}

		if (templateId && !rfxData) {
			const fetchedTemplateData = await getTemplateById(templateId);
			if (fetchedTemplateData) {
				const templateData = {
					...fetchedTemplateData.data,
					id: fetchedTemplateData.id,
					name: fetchedTemplateData.name,
					companyId: fetchedTemplateData.companyId,
				};
				updateLocalRfxData(templateData);

				const formValues = formValuesGenerator(templateData, true);
				formikRef?.current?.setValues(formValues);
			}
		}
		sethasTemplateLoaded(true);
	}, [templateId, rfxData, updateLocalRfxData]);

	// Fetch static options ONCE on mount
	useEffect(() => {
		let isMounted = true;
		setStaticOptionsLoaded(false);
		Promise.all([getRFQType(), getDocumentType()]).then(
			([types, docs, units]) => {
				if (!isMounted) return;
				setRFxTypes(types ?? []);
				setDocumentCategories(
					docs?.map((doc) => ({ label: doc.name, value: doc.id })) || []
				);
				setStaticOptionsLoaded(true);
			}
		);
		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		sethasTemplateLoaded(false);
		fetchTemplateDataOnce().finally(sethasTemplateLoaded(true));

	}, [templateId, fetchTemplateDataOnce]);

	const resetRfxData = useCallback(
		(rfx) => {
			if (!rfx) {
				setIsNotFound(true);
				return;
			}

			updateLocalRfxData(rfx);
			const formValues = formValuesGenerator(rfx);
			const isFormikLoaded = formikRef.current !== undefined;

			const mergedValues = isFormikLoaded
				? mergeCreateRFxFormData(
					formValues,
					selectedTab,
					formikRef.current.values
				)
				: formValues;

			formikRef?.current?.setValues(mergedValues);
		},
		[formikRef, selectedTab, updateLocalRfxData]
	);

	const handleSave = useCallback(
		// additional message is outside the form
		async (additional_message, type) => {
			const processContinueButton = (type) => {
				switch (type) {
					case "next":
						const currentIndex = tabButtonList.findIndex(
							(item) => item.value === selectedTab
						);
						const nextIndex = currentIndex + 1;
						if (tabButtonList?.length > nextIndex) {
							changeTab(tabButtonList[nextIndex]);
						}
						break;
					case "submit":
						navigate(clientPaths.rfx.manageTemplate);
						break;
					default:
						break;
				}
			};

			const values = formikRef?.current?.values;

			try {
				setIsSubmittingForm(true);
				// calculate document_ids regardless of template status
				const document_ids =
					values?.documents
						?.filter((doc) => !doc.isRemove) // Only include non-removed documents
						.map((doc) => doc.id)
						.filter(Boolean) || [];

				const template = {
					template_id: templateId,
					template_name:
						values?.template_name || values?.name || "New Template",
					document_ids: document_ids,
				};

				const submissionFormat = processFormValuesForSaving(
					additional_message,
					template,
					values
				);

				// Clean up explicitly undefined fields
				Object.keys(submissionFormat).forEach((key) => {
					if (submissionFormat[key] === undefined) {
						delete submissionFormat[key];
					}
				});

				const result =
					templateId === "new"
						? await createTemplate(values.template_name)
						: await saveRFXTemplate(
							submissionFormat,
							selectedTab,
							lastStep && openModal
						);

				if (result) {
					const currentTemplateId =
						(templateId !== "new" && templateId) || result.id;
					if (templateId === "new" || (!templateId && result.id)) {
						setTemplateId(result.id);
						replaceNewInUrlWithId(result.id);
					}

					const templateData = {
						...result.data,
						id: result.id,
						name: result.name,
						companyId: result.companyId,
						returnableScheduleFileId: result.data.returnableScheduleFile?.id,
					};
					updateLocalRfxData(templateData);
					const formValues = formValuesGenerator(templateData, true);
					formikRef?.current?.setValues(formValues);
					formikRef?.current?.submitForm();
					processContinueButton(type);
				} else {
					formikRef?.current?.submitForm();
					lastStep && openModal && setTouchedTabs([1, 2, 3, 4]);
					setOpenModal(false);
				}
				return true;
			} catch (e) {
				logErrorIfDebug("Error during handleSave:", e);
				setIsSubmittingForm(false);
				return false;
			} finally {
				setIsSubmittingForm(false);
			}
		},
		[
			lastStep,
			openModal,
			selectedTab,
			templateId,
			setTemplateId,
			company_id,
			tabButtonList,
			updateLocalRfxData,
		]
	);

	const handleSubmitOutside = async (type, additional_message) => {
		if (type === "laststep") {
			tabRef?.current?.scrollIntoView();
			checkIsCurrentStepValid(handleSave, additional_message, "submit");
		} else {
			let forceSave = selectedTab === 1 || selectedTab === 2;
			checkIsCurrentStepValid(null, null, null, false).then((isValid) => {
				if (forceSave || isValid) {
					handleSave(additional_message, type);
				} else {
					const tabName = TAB_NAMES[selectedTab - 1];
					showToast(
						`Complete all required fields`,
						`${tabName} Not Saved`,
						false,
						tabName
					);
				}
			});
		}
	};

	useEffect(() => {
		formikRef?.current?.validateForm();
		setTouchedTabs((prev) => {
			if (prev.includes(selectedTab)) {
				return prev;
			} else {
				return [...prev, selectedTab];
			}
		});
	}, [selectedTab]);

	useEffect(() => {
		if (!isSubmittingForm) {
			return;
		}

		formikRef?.current?.validateForm().then((errors) => {
			Object.keys(errors).forEach((fieldName) => {
				formikRef?.current?.setFieldTouched(fieldName, true);
			});
		});
	}, [isSubmittingForm]);

	useEffect(() => {
		const formValues = formValuesGenerator(rfxData, true);

		setCompletedSteps(
			[
				formValues?.template_name !== "" ? 1 : null,
				formValues?.documents.length > 0 ? 2 : null,
				formValues?.sections.filter((val) => val?.name !== "").length > 0
					? 3
					: formValues?.returnable_schedule_file
						? 3
						: null,
				formValues?.quote_type !== "file" &&
					formValues?.quote.filter((val) => val?.id !== null).length > 0
					? 4
					: formValues?.quote_file
						? 4
						: null,
			].filter((value) => value !== null)
		);

		if (rfxData && !isFormInitialized) {
			// only update form values if we're not in template mode or if we're editing a template
			if (isEdit) {
				formikRef?.current?.setValues(formValues);
			}

			setIsFormInitialized(true);
		}
	}, [rfxData, company_id, isEdit, isFormInitialized]);

	const changeTab = async (tab) => {
		setSelectedTab(tab.value);
	};

	const onBackButtonClicked = () => {
		const currentIndex = tabButtonList.findIndex(
			(item) => item.value === selectedTab
		);
		const prevIndex = currentIndex - 1;
		if (prevIndex >= 0) {
			changeTab(tabButtonList[prevIndex]);
		}
		tabRef?.current?.scrollIntoView();
	};

	const handleTabChangeWithSave = async (tab) => {
		if (isFileUploading) {
			return;
		}

		validateOnSave(() => changeTab(tab));
	};

	const validateOnSave = (onSuccess) => {
		// check current tab - if RfxDetail or documentaton, we want to enforce saving (regardless of validation result)
		let forceSave = selectedTab === 2;

		checkIsCurrentStepValid(null, selectedTab, null, false).then((isValid) => {
			if (forceSave || isValid) {
				// save and then change tab
				handleSave(undefined, "tabchange").then((result) => {
					// if enforce saving, ensure saved successfully
					((forceSave && result) || !forceSave) && onSuccess();
				});
			} else {
				const tabName = TAB_NAMES[selectedTab - 1];
				showToast(
					`You must complete all required fields.`,
					`${tabName} Not Saved`,
					false,
					tabName
				);
				onSuccess();
			}
		});
	};

	const collectTouchedPaths = (values, basePath = "") => {
		const paths = [];

		if (!values) {
			return paths;
		}

		// if answer is an array of values
		if (Array.isArray(values)) {
			// if we are at the base of the array and its empty or not an array of objects
			// mark the general field
			if (values.length === 0 || typeof values[0] !== "object") {
				// mark the array itself
				paths.push(basePath);
			}

			// now process each value in the array
			values.forEach((item, index) => {
				// if an objecttype, we need to keep going through the path to get to the 'value'
				if (typeof item === "object" && item !== null) {
					paths.push(...collectTouchedPaths(item, `${basePath}[${index}]`));
				} else {
					// primitive value: push the indexed path
					paths.push(`${basePath}[${index}]`);
				}
			});
		} else if (typeof values === "object") {
			Object.entries(values).forEach(([key, val]) => {
				const path = basePath ? `${basePath}.${key}` : key;

				if (key === "fields" && values.type === 10) {
					paths.push(path);

					if (val?.allow_document_categories) {
						paths.push(`${path}.allow_document_categories`);
					}
				} else {
					paths.push(path);
				}

				paths.push(...collectTouchedPaths(val, path));
			});
		}

		return paths;
	};

	// Helper function to recursively touch all fields in a nested structure
	const touchFieldsRecursively = (values, basePath, formik) => {
		const pathsToTouch = collectTouchedPaths(values, basePath);
		pathsToTouch.forEach((path) => formik.setFieldTouched(path, true, false));
	};

	const checkIsCurrentStepValid = async (
		handleOnStepValid,
		value,
		type,
		showValidationError = true
	) => {
		const currentForm = formikRef?.current;

		try {
			// Add small delay to ensure spinner appears --> Returnable schedules could be very long, treated specially
			if (selectedTab === 3) {
				await new Promise((resolve) => setTimeout(resolve, 100));
			}

			const errors = await currentForm?.validateForm();

			if (selectedTab === 3) {
				await new Promise((resolve) => {
					touchFieldsRecursively(
						currentForm.values.sections,
						"sections",
						currentForm
					);
					resolve();
				});
			} else {
				Object.keys(errors).forEach((fieldName) => {
					currentForm.setFieldTouched(fieldName, true);
				});
			}

			const updatedErrors = await currentForm.validateForm();

			const validationResult = validStepChecker(selectedTab, updatedErrors);

			const isCurrentStepValid =
				validationResult.isValid && touchedTabs.includes(selectedTab);

			if (isCurrentStepValid) {
				handleOnStepValid && (await handleOnStepValid(value, type));
			} else {
				showValidationError &&
					showToast(validationResult.errorMessage, "Error", false);
			}

			return isCurrentStepValid;
		} catch (error) {
			logErrorIfDebug("Error during validation:", error);
			showToast("An error occurred during validation", "Error", false);
		} finally {
			setIsSubmittingForm(false);
		}
	};

	const { values: formikValues } = formikRef.current || {}; // Get formik values safely

	// Calculate additional disabled condition for the last step of the template
	const isTemplateQuoteFormInvalid =
		lastStep &&
		formikValues?.quote_type === "form" &&
		isQuoteFormInvalid(formikValues?.quote);

	const hasLoaded = hasTemplateLoaded && staticOptionsLoaded;

	let blockSendingRFx = lastStep
		? !isValidForm ||
		isSubmittingForm ||
		isTemplateQuoteFormInvalid ||
		isFileUploading
		: isSubmittingForm || isFileUploading;

	return (
		<>
			{isNotFound && <NotFound />}
			{!isNotFound && (
				<div className="relative pt-4">
					<div ref={tabRef}>
						<CreateRFxHeaderButtons
							company_id={company_id}
							disableLastStep={blockSendingRFx}
							handleSubmitOutside={handleSubmitOutside}
							isEdit={isEdit}
							isLoading={!hasLoaded}
							isFileUploading={isFileUploading}
							isSubmittingForm={isSubmittingForm}
							isTemplate={true}
							isTemplateQuoteFormInvalid={isTemplateQuoteFormInvalid}
							isValidForm={isValidForm}
							lastStep={lastStep}
							loadRFQData={!hasLoaded}
							onOpen={onOpen}
							onOpenApproval={onApprovalOpen}
							rfqData={rfxData}
						/>
					</div>
					<div
						id="create-rfq-content"
						className="p-6"
					>
						<RFQTabNavigation
							isLoading={!hasLoaded}
							tabButtonList={tabButtonList}
							selectedTab={selectedTab}
							completedSteps={completedSteps}
							touchedTabs={touchedTabs}
							formikRef={formikRef}
							isTemplate={true}
							isEdit={isEdit}
							rfqData={rfxData}
							onChangeTab={handleTabChangeWithSave}
						/>

						{<SavingOverlay isShown={isSubmittingForm} />}

						{hasLoaded ? (
							<Formik
								onSubmit={() => { }}
								initialValues={
									true
										? rfxData
											? formValuesGenerator(rfxData, true)
											: formValuesGenerator(null, true)
										: formValuesGenerator(rfxData, true)
								}
								innerRef={formikRef}
								validationSchema={validationSchema(true, rfxData?.rfxType)}
								validateOnChange={enableOnChangeFormik}
								validateOnMount={true}
							>
								{({
									isSubmitting,
									isValid,
									errors,
									values,
									setFieldError,
									setFieldValue,
								}) => {
									blockSendingRFx = blockSendingRFx || (!isValid && lastStep);
									if (isValid !== isValidForm) {
										setIsValidForm(isValid);
									}
									return (
										<Form>
											{selectedTab === 1 &&
												(true ? (
													<TemplateDetail
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														data={null}
														isEdit={isEdit}
													/>
												) : (
													<ProjectDetail
														titlePage={activeTab.title}
														subTitlePage={activeTab.subTitle}
														rfxData={rfxData}
														rfxTypes={rfxTypes}
													/>
												))}
											{selectedTab === 2 && (
												<Documentation
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													documentCategories={documentCategories}
													isTemplate={true}
													rfxOrRfxTemplateId={templateId}
												/>
											)}
											{selectedTab === 3 && (
												<ReturnableSchedule
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													setFieldError={setFieldError}
													setFieldValue={setFieldValue}
													isTemplate={true}
													rfxOrRfxTemplateId={templateId}
													previewRfxHandler={onOpen}
												/>
											)}
											{selectedTab === 4 && (
												<Quote
													titlePage={activeTab.title}
													subTitlePage={activeTab.subTitle}
													setFieldValue={setFieldValue}
													isTemplate={true}
													rfxOrRfxTemplateId={templateId}
												/>
											)}
											<Flex
												gap={4}
												align={"center"}
												className="float-end py-10"
											>
												{!true && (
													<TextButton
														disabled={isSubmittingForm || isFileUploading}
														onClick={() => handleSubmitOutside("save")}
													>
														Save Draft
													</TextButton>
												)}
												{selectedTab > 1 && (
													<Button
														disabled={isSubmittingForm || isFileUploading}
														btntype={"base"}
														type="button"
														onClick={onBackButtonClicked}
													>
														Back
													</Button>
												)}
												<ContinueButton
													onClick={() =>
														handleSubmitOutside(lastStep ? "laststep" : "next")
													}
													disabled={blockSendingRFx}
													isTemplate={true}
													lastStep={lastStep}
												/>
											</Flex>
											{isOpen && rfxData && (
												<PreviewRFQ
													isOpen={isOpen}
													onClose={onClose}
													rfqData={rfxData}
													documentCategories={documentCategories}
													rfqTypes={rfxTypes}
													isTemplate={true}
												/>
											)}
										</Form>
									);
								}}
							</Formik>
						) : (
							<div className="min-h-[70vh]">
								<LoadingSpinner label={"Loading..."} />
							</div>
						)}
						{!true && (
							<InvitePreApprovalDrawer
								rfqName={rfxData?.name}
								onClose={onApprovalClose}
								isOpen={isApprovalOpen}
								rfx={rfxData}
							/>
						)}
					</div>
				</div>
			)}
		</>
	);
};

export default CreateRfxTemplate;
